import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { TabsContent as TC } from '@/components/ui/tabs'; // alias not used; kept for clarity
import { toast } from 'sonner';
import { Save, Plus, Trash2, RefreshCw, AlertTriangle } from 'lucide-react';

export function AttendanceRulesTab() {
  const [tab, setTab] = useState('rules');

  // Rules state
  const [rules, setRules] = useState(null);
  const [rulesDraft, setRulesDraft] = useState({});
  const [saving, setSaving] = useState(false);

  // Public holidays state
  const [holidays, setHolidays] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loadingPH, setLoadingPH] = useState(false);
  const [newPH, setNewPH] = useState({ date: '', name: '' });

  const fetchRules = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/attendance/rules`);
      setRules(r.data);
      setRulesDraft(r.data);
    } catch (e) { toast.error('Failed to load rules'); }
  }, []);

  const fetchHolidays = useCallback(async () => {
    setLoadingPH(true);
    try {
      const r = await axios.get(`${API}/attendance/public-holidays`, { params: { year } });
      setHolidays(r.data.holidays || []);
    } catch (e) { toast.error('Failed to load public holidays'); }
    finally { setLoadingPH(false); }
  }, [year]);

  useEffect(() => { fetchRules(); }, [fetchRules]);
  useEffect(() => { if (tab === 'holidays') fetchHolidays(); }, [tab, fetchHolidays]);

  const saveRules = async () => {
    setSaving(true);
    try {
      const payload = {
        rounding_minutes: parseInt(rulesDraft.rounding_minutes ?? 15, 10),
        rounding_enabled: !!rulesDraft.rounding_enabled,
        daily_ot_threshold_5day: parseFloat(rulesDraft.daily_ot_threshold_5day ?? 9.0),
        daily_ot_threshold_6day: parseFloat(rulesDraft.daily_ot_threshold_6day ?? 8.0),
        weekly_ot_threshold: parseFloat(rulesDraft.weekly_ot_threshold ?? 45.0),
        sunday_separate_bucket: !!rulesDraft.sunday_separate_bucket,
        public_holiday_separate_bucket: !!rulesDraft.public_holiday_separate_bucket,
        tz_offset_minutes_default: parseInt(rulesDraft.tz_offset_minutes_default ?? 120, 10),
        night_shift_cutoff_time: (rulesDraft.night_shift_cutoff_time || '19:00').slice(0, 5),
        night_shift_allowance: parseFloat(rulesDraft.night_shift_allowance ?? 30.0),
      };
      const r = await axios.put(`${API}/attendance/rules`, payload);
      setRules(r.data);
      setRulesDraft(r.data);
      toast.success('Rules saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const addPH = async () => {
    if (!newPH.date || !newPH.name) { toast.error('Date and name required'); return; }
    try {
      await axios.post(`${API}/attendance/public-holidays`, { date: newPH.date, name: newPH.name });
      toast.success('Public holiday added');
      setNewPH({ date: '', name: '' });
      fetchHolidays();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const toggleActive = async (ph) => {
    try {
      await axios.put(`${API}/attendance/public-holidays/${ph.id}`, { is_active: !ph.is_active });
      fetchHolidays();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const renamePH = async (ph, name) => {
    try {
      await axios.put(`${API}/attendance/public-holidays/${ph.id}`, { name });
      fetchHolidays();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const deletePH = async (ph) => {
    if (!window.confirm(`Remove "${ph.name}" on ${ph.date}?`)) return;
    try {
      await axios.delete(`${API}/attendance/public-holidays/${ph.id}`);
      toast.success('Removed');
      fetchHolidays();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const reseedSA = async () => {
    if (!window.confirm('Re-seed missing SA statutory holidays for 2024–2028?')) return;
    try {
      const r = await axios.post(`${API}/attendance/public-holidays/seed-sa`);
      toast.success(`Added ${r.data.inserted} statutory holiday(s)`);
      fetchHolidays();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const dirty = useMemo(() => {
    if (!rules) return false;
    const keys = [
      'rounding_minutes', 'rounding_enabled',
      'daily_ot_threshold_5day', 'daily_ot_threshold_6day', 'weekly_ot_threshold',
      'sunday_separate_bucket', 'public_holiday_separate_bucket',
      'tz_offset_minutes_default',
      'night_shift_cutoff_time', 'night_shift_allowance',
    ];
    return keys.some(k => String(rules[k]) !== String(rulesDraft[k]));
  }, [rules, rulesDraft]);

  return (
    <div className="space-y-4" data-testid="attendance-rules-tab">
      <div className="bg-white rounded-xl border border-stone-200 p-4">
        <h2 className="text-lg font-semibold text-stone-800">Attendance Rules & Public Holidays</h2>
        <p className="text-xs text-stone-500 mt-1">
          Overtime is weekly only — Mon-Sat hours above 45 in a Mon-Sun week, reported in the payroll month the week concludes in. Sunday and
          Public Holiday hours are tracked separately. These buckets are for classification only;
          payroll rates and multipliers stay in your payroll system.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="rules" data-testid="rules-subtab-rules">Rules</TabsTrigger>
          <TabsTrigger value="holidays" data-testid="rules-subtab-holidays">Public Holidays</TabsTrigger>
        </TabsList>

        <TabsContent value="rules">
          {!rules && <div className="text-stone-400 text-sm">Loading…</div>}
          {rules && (
            <div className="bg-white rounded-xl border border-stone-200 p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Rounding (minutes)</Label>
                  <Input type="number" min="0" max="60" step="1"
                    value={rulesDraft.rounding_minutes ?? 15}
                    onChange={e => setRulesDraft({ ...rulesDraft, rounding_minutes: e.target.value })}
                    data-testid="rules-rounding-minutes" />
                  <p className="text-xs text-stone-500 mt-1">Total hours round DOWN to the nearest N minutes. Set to 0 to disable.</p>
                </div>
                <div className="flex items-end gap-3">
                  <div>
                    <Label className="text-xs">Rounding enabled</Label>
                    <div className="flex items-center gap-2 mt-2">
                      <Switch checked={!!rulesDraft.rounding_enabled}
                        onCheckedChange={v => setRulesDraft({ ...rulesDraft, rounding_enabled: v })}
                        data-testid="rules-rounding-enabled" />
                      <span className="text-sm">{rulesDraft.rounding_enabled ? 'ON' : 'OFF'}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Weekly OT threshold (hours)</Label>
                  <Input type="number" step="0.5" min="0"
                    value={rulesDraft.weekly_ot_threshold ?? 45}
                    onChange={e => setRulesDraft({ ...rulesDraft, weekly_ot_threshold: e.target.value })}
                    data-testid="rules-weekly-ot" />
                </div>
                <div>
                  <Label className="text-xs">Default device timezone (minutes from UTC)</Label>
                  <Input type="number" step="15"
                    value={rulesDraft.tz_offset_minutes_default ?? 120}
                    onChange={e => setRulesDraft({ ...rulesDraft, tz_offset_minutes_default: e.target.value })}
                    data-testid="rules-tz-default" />
                  <p className="text-xs text-stone-500 mt-1">SAST = 120. Used when a new device first connects.</p>
                </div>
                <div className="md:col-span-2 border-t border-stone-200 pt-3 mt-1">
                  <div className="text-xs font-semibold text-stone-700 mb-2 uppercase tracking-wide">Night shift allowance</div>
                  <p className="text-xs text-stone-500 mb-3">
                    Flat rand amount paid per shift where clock-out is <b>after</b> the cut-off time. Applies to all roles. Set the allowance to R0 to disable the feature.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs">Night-shift cut-off time</Label>
                      <Input type="time"
                        value={(rulesDraft.night_shift_cutoff_time || '19:00').slice(0, 5)}
                        onChange={e => setRulesDraft({ ...rulesDraft, night_shift_cutoff_time: e.target.value })}
                        data-testid="rules-night-cutoff" />
                      <p className="text-xs text-stone-500 mt-1">e.g. <code>19:00</code> — clock-outs strictly later than this qualify.</p>
                    </div>
                    <div>
                      <Label className="text-xs">Allowance per qualifying shift (R)</Label>
                      <Input type="number" step="1" min="0"
                        value={rulesDraft.night_shift_allowance ?? 30}
                        onChange={e => setRulesDraft({ ...rulesDraft, night_shift_allowance: e.target.value })}
                        data-testid="rules-night-allowance" />
                      <p className="text-xs text-stone-500 mt-1">Currently R30 in production defaults.</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Switch checked={!!rulesDraft.sunday_separate_bucket}
                    onCheckedChange={v => setRulesDraft({ ...rulesDraft, sunday_separate_bucket: v })}
                    data-testid="rules-sunday-bucket" />
                  <div>
                    <div className="text-sm font-medium">Sundays in separate bucket</div>
                    <div className="text-xs text-stone-500">When ON, all Sunday hours go to <code>sunday_hours</code> instead of ordinary/OT.</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Switch checked={!!rulesDraft.public_holiday_separate_bucket}
                    onCheckedChange={v => setRulesDraft({ ...rulesDraft, public_holiday_separate_bucket: v })}
                    data-testid="rules-ph-bucket" />
                  <div>
                    <div className="text-sm font-medium">Public holidays in separate bucket</div>
                    <div className="text-xs text-stone-500">When ON, all PH hours go to <code>public_holiday_hours</code>.</div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-stone-200">
                <Button variant="ghost" onClick={() => setRulesDraft(rules)} disabled={!dirty}>Reset</Button>
                <Button onClick={saveRules} disabled={!dirty || saving}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="save-rules-button">
                  <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : 'Save rules'}
                </Button>
              </div>
              <div className="text-xs text-amber-800 bg-amber-50 p-2 rounded flex gap-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                Rule changes apply to <b>new</b> punches automatically. To re-classify existing records, run <b>Biometric → User Mapping → Re-resolve unmapped</b>, or trigger <code>POST /api/biometric/rebuild-attendance</code>.
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="holidays">
          <div className="bg-white rounded-xl border border-stone-200 p-3 mb-3 flex items-center gap-3 flex-wrap">
            <Label className="text-xs">Year</Label>
            <Input type="number" min="2020" max="2040" value={year}
              onChange={e => setYear(parseInt(e.target.value || '2026', 10))}
              className="w-24" data-testid="ph-year" />
            <Button variant="outline" size="sm" onClick={fetchHolidays}>
              <RefreshCw className={`w-3 h-3 mr-1 ${loadingPH ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={reseedSA} data-testid="reseed-sa-button">
              Re-seed SA statutory holidays
            </Button>
            <div className="ml-auto flex items-end gap-2">
              <div>
                <Label className="text-xs">Add custom date</Label>
                <Input type="date" value={newPH.date} onChange={e => setNewPH({ ...newPH, date: e.target.value })}
                  data-testid="new-ph-date" />
              </div>
              <div>
                <Label className="text-xs">Name</Label>
                <Input value={newPH.name} onChange={e => setNewPH({ ...newPH, name: e.target.value })}
                  placeholder="Company shutdown" data-testid="new-ph-name" />
              </div>
              <Button onClick={addPH} className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="add-ph-button">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-stone-100 text-stone-700">
                <tr>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">Day</th>
                  <th className="text-left px-3 py-2">Name</th>
                  <th className="text-left px-3 py-2">Source</th>
                  <th className="text-center px-3 py-2">Active</th>
                  <th className="text-right px-3 py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {holidays.length === 0 && !loadingPH && (
                  <tr><td colSpan={6} className="text-center text-stone-400 py-6">No public holidays in {year}.</td></tr>
                )}
                {holidays.map(ph => {
                  const d = new Date(ph.date);
                  const day = d.toLocaleDateString('en-ZA', { weekday: 'short' });
                  return (
                    <tr key={ph.id} className="border-t border-stone-100 hover:bg-stone-50">
                      <td className="px-3 py-2 font-mono">{ph.date}</td>
                      <td className="px-3 py-2 text-xs">{day}</td>
                      <td className="px-3 py-2">
                        <input defaultValue={ph.name}
                          onBlur={e => { if (e.target.value !== ph.name) renamePH(ph, e.target.value); }}
                          className="border border-stone-200 rounded px-2 py-1 text-sm w-72" />
                      </td>
                      <td className="px-3 py-2 text-xs text-stone-600">
                        {ph.source === 'statutory_sa' ? 'SA statutory' : 'Custom'}
                        {ph.is_observed && ' · observed'}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <Switch checked={!!ph.is_active} onCheckedChange={() => toggleActive(ph)}
                          data-testid={`toggle-ph-${ph.id}`} />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button variant="ghost" size="sm" className="text-rose-600"
                          onClick={() => deletePH(ph)} data-testid={`delete-ph-${ph.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default AttendanceRulesTab;
